// src/pages/orgvisits/OrgVisitsList.js
import React, { useState, useEffect } from 'react';
import { OverlayTrigger, Button, Modal, Row, Col, Form, Card } from 'react-bootstrap';
import axios from 'axios';
import { Tooltip } from '@mui/material';
import { 
  MdFilterList, 
  MdAddCircle, 
  MdEdit, 
  MdDelete, 
  MdSearch,
  MdCheckCircle,
  MdPendingActions,
  MdToday,
  MdBusinessCenter,
  MdOutlineMarkChatRead,
  MdInfo
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import loading from '../../assets/images/common/loading.gif';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AppDataGrid from '../../components/common/AppDataGrid';
import dayjs from 'dayjs';
import './orgvisits.css';


const OrgVisitsList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visits, setVisits] = useState([]);

  const userStr = sessionStorage.getItem("user");
  const userObj = userStr ? JSON.parse(userStr) : {};
  const school_id = userObj.school_id || null;

  // Filter States
  const [filterForm, setFilterForm] = useState({
    org_name: '',
    purpose: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({
    org_name: '',
    purpose: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  // Load Data
  useEffect(() => {
    const fetchVisits = async () => {
      setIsLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const response = await axios.post(`${baseUrl}/orgvisits/`, {
          action: 'READ',
          school_id: school_id
        });
        setVisits(response.data);
      } catch (error) {
        console.error("Error fetching organization visits:", error);
        toast.error("Failed to load organization visits.");
        setVisits([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchVisits();
  }, [school_id]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleCloseFilterModal = () => {
    setFilterForm(appliedFilters);
    setShowFilterModal(false);
  };
  const handleShowFilterModal = () => setShowFilterModal(true);

  const handleFilterInputChange = (e) => {
    const { id, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleResetFilters = (e) => {
    e.preventDefault();
    const cleared = {
      org_name: '',
      purpose: '',
      status: '',
      startDate: '',
      endDate: ''
    };
    setFilterForm(cleared);
    setAppliedFilters(cleared);
    toast.info('Filters Reset Successfully');
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    if (filterForm.startDate && filterForm.endDate && dayjs(filterForm.startDate).isAfter(dayjs(filterForm.endDate))) {
      toast.error('Start date cannot be after End date.');
      return;
    }
    setAppliedFilters(filterForm);
    setShowFilterModal(false);
    toast.success('Filters Applied Successfully');
  };

  const handleDeleteClick = async (visit_id) => {
    const ok = window.confirm('Are you sure you want to delete this organization visit log?');
    if (!ok) return;
    
    try {
      setIsLoading(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      await axios.post(`${baseUrl}/orgvisits/`, {
        action: 'DELETE',
        visit_id: visit_id
      });
      setVisits((prev) => prev.filter((v) => v.visit_id !== visit_id));
      toast.success('Visit Log Deleted Successfully');
    } catch (error) {
      console.error("Error deleting organization visit log:", error);
      toast.error("Failed to delete organization visit log.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (visit) => {
    navigate('/addorgvisit', { state: { visitData: visit } });
  };

  const handleViewDetails = (visit) => {
    setSelectedVisit(visit);
    setShowDetailModal(true);
  };

  const titleCase = (s = '') =>
    String(s).replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

  // Statistics Computations
  const totalVisits = visits.length;
  const todayDateStr = dayjs().format('YYYY-MM-DD');
  const todayVisits = visits.filter((v) => v.visit_date === todayDateStr).length;
  const pendingFollowups = visits.filter((v) => v.followup_required && v.status !== 'Completed').length;
  const completedVisits = visits.filter((v) => v.status === 'Completed').length;

  const columns = [
    {
      field: 'org_name',
      headerName: 'Organization',
      flex: 1.2,
      renderCell: (p) => (
        <Tooltip title={p.value}>
          <span style={{ fontWeight: 600, color: '#303133' }}>{p.value}</span>
        </Tooltip>
      ),
      sortable: true
    },
    {
      field: 'visitor_name',
      headerName: 'Representative',
      flex: 1,
      renderCell: (p) => <Tooltip title={p.value}><span>{p.value}</span></Tooltip>,
      sortable: true
    },
    {
      field: 'purpose',
      headerName: 'Purpose',
      flex: 0.9,
      renderCell: (p) => (
        <span className="badge bg-light text-dark border px-2 py-1" style={{ borderRadius: '6px' }}>
          {p.value}
        </span>
      ),
      sortable: true
    },
    {
      field: 'visit_date',
      headerName: 'Date',
      flex: 0.8,
      renderCell: (p) => <span>{dayjs(p.value).format('DD MMM YYYY')}</span>,
      sortable: true
    },
    {
      field: 'timings',
      headerName: 'Timings',
      flex: 0.9,
      renderCell: (p) => (
        <span style={{ fontSize: '13px', color: '#606266' }}>
          {p.row.arrival_time} - {p.row.departure_time}
        </span>
      )
    },
    {
      field: 'followup_required',
      headerName: 'Follow-up?',
      flex: 0.7,
      renderCell: (p) => (
        <span className={`badge ${p.value ? 'bg-warning text-dark' : 'bg-secondary'} px-2 py-1`}>
          {p.value ? 'Required' : 'None'}
        </span>
      ),
      sortable: true
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (p) => {
        let badgeClass = 'bg-danger';
        if (p.value === 'Completed') badgeClass = 'bg-success';
        if (p.value === 'In Progress') badgeClass = 'bg-info text-dark';
        if (p.value === 'Pending') badgeClass = 'bg-warning text-dark';

        return (
          <span className={`badge ${badgeClass} px-2 py-1`} style={{ borderRadius: '6px' }}>
            {p.value}
          </span>
        );
      },
      sortable: true
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      renderCell: (params) => (
        <div className="tableActions">
          <Tooltip title="View Details">
            <span className="commonActionIcons" onClick={() => handleViewDetails(params.row)}>
              <MdInfo style={{ color: '#3a7bd5' }} />
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <span className="commonActionIcons" onClick={() => handleEditClick(params.row)}>
              <MdEdit style={{ color: '#e6a23c' }} />
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span className="commonActionIcons deleteIcon" onClick={() => handleDeleteClick(params.row.visit_id)}>
              <MdDelete style={{ color: '#f56c6c' }} />
            </span>
          </Tooltip>
        </div>
      )
    }
  ];

  // Grid Query Filtering Logic
  const toStr = (v) => (v ?? '').toString();
  const lower = (s) => s.toLowerCase();
  const stripNonAlnum = (s) => s.replace(/[^a-z0-9]/gi, '');

  const qRaw = searchQuery.trim();
  const q1 = lower(qRaw);
  const q2 = stripNonAlnum(q1);

  const fieldMatches = (value) => {
    const v = toStr(value);
    const v1 = lower(v);
    const v2 = stripNonAlnum(v1);
    return (q1 && v1.includes(q1)) || (q2 && v2.includes(q2));
  };

  const rows = (visits || []).filter((r) => {
    // 1. Instant Search Text Match
    if (q1 || q2) {
      const candidates = [
        r.org_name,
        r.visitor_name,
        r.purpose,
        r.details,
        r.followup_action
      ];
      if (!candidates.some(fieldMatches)) return false;
    }

    // 2. Filter Modal Field Matches
    if (appliedFilters.org_name && !lower(r.org_name).includes(lower(appliedFilters.org_name))) return false;
    if (appliedFilters.purpose && r.purpose !== appliedFilters.purpose) return false;
    if (appliedFilters.status) {
      if (appliedFilters.status === 'No Follow-up') {
        if (r.followup_required) return false;
      } else {
        if (r.status !== appliedFilters.status) return false;
      }
    }
    if (appliedFilters.startDate && dayjs(r.visit_date).isBefore(dayjs(appliedFilters.startDate))) return false;
    if (appliedFilters.endDate && dayjs(r.visit_date).isAfter(dayjs(appliedFilters.endDate))) return false;

    return true;
  });

  return (
    <div className="pageBody">
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />

      {/* Premium Analytics Metric Widgets Row */}
      <Row className="metrics-row g-4">
        <Col xs={12} sm={6} lg={3}>
          <div className="metric-card gradient-blue">
            <div className="metric-card-header">
              <span className="metric-card-title">Total Visits</span>
              <MdBusinessCenter className="metric-card-icon" />
            </div>
            <h3 className="metric-card-value">{totalVisits}</h3>
            <span className="metric-card-sub">All logged organizations</span>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <div className="metric-card gradient-purple">
            <div className="metric-card-header">
              <span className="metric-card-title">Today's Visits</span>
              <MdToday className="metric-card-icon" />
            </div>
            <h3 className="metric-card-value">{todayVisits}</h3>
            <span className="metric-card-sub">Logged during today</span>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <div className="metric-card gradient-amber">
            <div className="metric-card-header">
              <span className="metric-card-title">Pending Follow-ups</span>
              <MdPendingActions className="metric-card-icon" />
            </div>
            <h3 className="metric-card-value">{pendingFollowups}</h3>
            <span className="metric-card-sub">Action required soon</span>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <div className="metric-card gradient-emerald">
            <div className="metric-card-header">
              <span className="metric-card-title">Completed Cases</span>
              <MdCheckCircle className="metric-card-icon" />
            </div>
            <h3 className="metric-card-value">{completedVisits}</h3>
            <span className="metric-card-sub">Resolved and archived</span>
          </div>
        </Col>
      </Row>

      {/* Header and Grid Toolbar */}
      <div className="commonDataTableHead">
        <div className="d-flex flex-wrap justify-content-between align-items-center w-100 gap-3">
          <div className="d-flex align-items-center">
            <h6 className="commonTableTitle" style={{ margin: 0 }}>
              Organization Visits Log
            </h6>
          </div>
          <div className="d-flex align-items-center gap-2 flex-grow-1 justify-content-md-end justify-content-start">
            <div className="">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                className="searchInput"
                onChange={handleSearchChange}
              />
            </div>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-filter">Filter</Tooltip>}>
              <Button className="secondaryBtn" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-add">Add</Tooltip>}>
              <Button className="primaryBtn" onClick={() => navigate('/addorgvisit')}>
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      {/* Main Grid Card Section */}
      <div className="commonTable">
        <div className="tableBody" style={{ minHeight: '350px' }}>
          {isLoading ? (
            <div className="loadingContainer d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
              <img src={loading} alt="Loading..." className="loadingGif" style={{ width: '60px' }} />
            </div>
          ) : (
            <AppDataGrid
              rows={rows}
              columns={columns}
              getRowId={(r) => r.visit_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title style={{ fontSize: '16px', fontWeight: 600 }}>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleApplyFilters}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>Organization Name</Form.Label>
                    <Form.Control
                      type="text"
                      id="org_name"
                      value={filterForm.org_name}
                      placeholder="Enter organization keyword..."
                      onChange={handleFilterInputChange}
                      maxLength={40}
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>Purpose of Visit</Form.Label>
                    <Form.Select
                      id="purpose"
                      value={filterForm.purpose}
                      onChange={handleFilterInputChange}
                    >
                      <option value="">All Purposes</option>
                      <option value="Audit/Inspection">Audit/Inspection</option>
                      <option value="Partnership">Partnership</option>
                      <option value="General Enquiry">General Enquiry</option>
                      <option value="Service/Repair">Service/Repair</option>
                      <option value="Guest Lecture">Guest Lecture</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>Follow-up Status</Form.Label>
                    <Form.Select
                      id="status"
                      value={filterForm.status}
                      onChange={handleFilterInputChange}
                    >
                      <option value="">All Statuses</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="No Follow-up">No Follow-up</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      id="startDate"
                      value={filterForm.startDate}
                      onChange={handleFilterInputChange}
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      id="endDate"
                      value={filterForm.endDate}
                      onChange={handleFilterInputChange}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleResetFilters}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="btn-success primaryBtn" form="filterForm" onClick={handleApplyFilters}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Details Dialog / Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#f8f9fa' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            <MdBusinessCenter style={{ marginRight: '8px', color: '#3a7bd5' }} /> 
            Visit details: {selectedVisit?.org_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', padding: '24px' }}>
          {selectedVisit && (
            <div>
              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <p className="mb-2"><strong>Representative Name:</strong> {selectedVisit.visitor_name}</p>
                  <p className="mb-2"><strong>Contact Information:</strong> {selectedVisit.contact_info || 'Not Provided'}</p>
                  <p className="mb-2"><strong>Purpose of Visit:</strong> {selectedVisit.purpose}</p>
                </Col>
                <Col xs={12} md={6}>
                  <p className="mb-2"><strong>Visit Date:</strong> {dayjs(selectedVisit.visit_date).format('DD MMMM YYYY')}</p>
                  <p className="mb-2"><strong>Timings:</strong> {selectedVisit.arrival_time} - {selectedVisit.departure_time}</p>
                  <p className="mb-2">
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${selectedVisit.status === 'Completed' ? 'bg-success' : selectedVisit.status === 'In Progress' ? 'bg-info text-dark' : 'bg-warning text-dark'} px-2 py-1`}>
                      {selectedVisit.status}
                    </span>
                  </p>
                </Col>
              </Row>
              <hr />
              <div className="mb-4">
                <h6 style={{ fontWeight: 600, color: '#303133', marginBottom: '8px' }}>Discussions & Details</h6>
                <div style={{ padding: '14px', background: '#f5f7fa', borderRadius: '8px', border: '1px solid #e4e7ed', color: '#4d4f53', fontSize: '14px' }}>
                  {selectedVisit.details || 'No details registered.'}
                </div>
              </div>
              
              {selectedVisit.followup_required && (
                <div className="followup-section" style={{ display: 'block' }}>
                  <h6 style={{ fontWeight: 600, color: '#e6a23c', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MdOutlineMarkChatRead /> Required Follow-up Action
                  </h6>
                  <p className="mb-2"><strong>Target Date:</strong> {dayjs(selectedVisit.followup_target_date).format('DD MMMM YYYY')}</p>
                  <div style={{ padding: '12px', background: '#ffffff', borderRadius: '6px', border: '1px solid rgba(245, 175, 25, 0.3)', color: '#606266', fontSize: '13px' }}>
                    {selectedVisit.followup_action}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#f8f9fa' }}>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)} className="secondaryBtn">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgVisitsList;
